import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'qrcode.react';
import axios from 'axios';
import './App.css';

function App() {
  const [oauthData, setOauthData] = useState(null);
  const [status, setStatus] = useState('idle');
  const [pollingInterval, setPollingInterval] = useState(null);
  const [pollingTimer, setPollingTimer] = useState(null);

  const [debugLogs, setDebugLogs] = useState([]);
  const [expirationTimer, setExpirationTimer] = useState(null);
  
  // App navigation state
  const [currentPage, setCurrentPage] = useState('apps'); // 'apps', 'app-details', 'oauth', 'welcome'
  const [selectedApp, setSelectedApp] = useState(null);
  const [userName, setUserName] = useState(null);

  // TV Power On Effect
  const [tvPowerOn, setTvPowerOn] = useState(false);
  const [tvScreenVisible, setTvScreenVisible] = useState(false);
  
  // Timer state
  const [timeLeft, setTimeLeft] = useState(0);

  // Sample apps data
  const apps = [
    {
      id: 1,
      name: 'NationalPost',
      icon: '🎬',
      description: 'Stream your favorite movies and TV shows',
      color: '#E50914'
    },
    {
      id: 2,
      name: 'YouTube',
      icon: '📺',
      description: 'Watch videos and live streams',
      color: '#FF0000'
    },
    {
      id: 3,
      name: 'Spotify',
      icon: '🎵',
      description: 'Listen to music and podcasts',
      color: '#1DB954'
    },
    {
      id: 4,
      name: 'Disney+',
      icon: '🏰',
      description: 'Disney, Pixar, Marvel, and Star Wars',
      color: '#0063E5'
    },
    {
      id: 5,
      name: 'Prime Video',
      icon: '📦',
      description: 'Amazon Prime Video streaming',
      color: '#00A8E1'
    },
    {
      id: 6,
      name: 'Hulu',
      icon: '🟢',
      description: 'Stream TV shows and movies',
      color: '#1CE783'
    }
  ];

  // Navigation functions
  const handleAppSelect = (app) => {
    setSelectedApp(app);
    setCurrentPage('app-details');
  };

  const handleLogin = () => {
    setCurrentPage('oauth');
    // Start OAuth flow immediately when login button is clicked
    generateDeviceCode();
  };

  const handleBackToApps = () => {
    // Stop all polling
    stopPolling();
    
    // Clear expiration timer
    if (expirationTimer) {
      clearTimeout(expirationTimer);
      setExpirationTimer(null);
    }
    
    // Reset OAuth state
    setOauthData(null);
    setStatus('idle');
    setPollingInterval(null);
    
    // Clear all debug logs
    setDebugLogs([]);
    
    // Navigate back to apps page
    setCurrentPage('apps');
    setSelectedApp(null);
    
    // Add a log entry to indicate Home button was clicked
    addDebugLog('INFO', 'Home button clicked - All polling stopped and logs cleared');
  };

  // Timer effect
  useEffect(() => {
    if (oauthData && oauthData.expiresIn) {
      const updateTimer = () => {
        const remaining = Math.max(0, Math.floor((oauthData.expiresIn - Date.now()) / 1000));
        setTimeLeft(remaining);
      };
      
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      
      return () => clearInterval(interval);
    }
  }, [oauthData]);

  // Function to extract user name from ID token
  const extractUserNameFromToken = (accessToken) => {
    try {
      // Decode the JWT token (access token contains user info)
      const tokenParts = accessToken.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        return payload.name || payload.given_name || payload.email?.split('@')[0] || 'User';
      }
    } catch (error) {
      console.error('Error extracting user name from token:', error);
    }
    return 'User';
  };

  // Function to get user info from userinfo endpoint
  const getUserInfo = async (accessToken) => {
    try {
      addDebugLog('REQUEST', 'Calling userinfo endpoint', {
        url: OAUTH_CONFIG.userinfoUrl,
        accessToken: accessToken.substring(0, 20) + '...'
      });

      const response = await axios.get(OAUTH_CONFIG.userinfoUrl, {
        params: {
          access_token: accessToken
        },
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      addDebugLog('SUCCESS', 'Userinfo response received', response.data);
      
      // Extract name from userinfo response
      const name = response.data.name || 
                   response.data.given_name || 
                   response.data.email?.split('@')[0] || 
                   'User';
      
      return name;
    } catch (error) {
      console.error('Error calling userinfo endpoint:', error);
      addDebugLog('ERROR', 'Userinfo endpoint call failed', {
        error: error.message,
        response: error.response?.data
      });
      
      // Fallback to extracting from token if userinfo fails
      return extractUserNameFromToken(accessToken);
    }
  };

  // OAuth configuration from environment variables
  const OAUTH_CONFIG = {
    deviceAuthUrl: process.env.REACT_APP_OAUTH_DEVICE_AUTH_URL || 'https://oauth2.googleapis.com/device/code',
    tokenUrl: process.env.REACT_APP_OAUTH_TOKEN_URL || 'https://oauth2.googleapis.com/token',
    userinfoUrl: process.env.REACT_APP_OAUTH_USERINFO_URL || 'https://www.googleapis.com/oauth2/v2/userinfo',
    clientId: process.env.REACT_APP_OAUTH_CLIENT_ID || 'your-client-id-here',
    scope: process.env.REACT_APP_OAUTH_SCOPE || 'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email'
  };

  // TV Power On Effect
  useEffect(() => {
    // Start TV power on sequence
    setTimeout(() => {
      setTvPowerOn(true);
    }, 500);

    // Show TV screen after power on effect
    setTimeout(() => {
      setTvScreenVisible(true);
    }, 2000);
  }, []);

  // Debug: Log environment variables
  console.log('Environment Variables:', {
    clientId: process.env.REACT_APP_OAUTH_CLIENT_ID,
    deviceAuthUrl: process.env.REACT_APP_OAUTH_DEVICE_AUTH_URL,
    tokenUrl: process.env.REACT_APP_OAUTH_TOKEN_URL,
    userinfoUrl: process.env.REACT_APP_OAUTH_USERINFO_URL,
    scope: process.env.REACT_APP_OAUTH_SCOPE
  });
  console.log('OAUTH_CONFIG:', OAUTH_CONFIG);

  // Debug logging function
  const addDebugLog = useCallback((type, message, data = null) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp,
      type,
      message,
      data
    };
    setDebugLogs(prev => [logEntry, ...prev.slice(0, 999)]); // Keep last 100 logs
  }, []);

  // Combined request/response logging function
  const addRequestResponseLog = useCallback((requestData, responseData, error = null) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp,
      type: 'request-response',
      message: error ? 'API Call Failed' : 'API Call Successful',
      request: requestData,
      response: responseData,
      error: error
    };
    setDebugLogs(prev => [logEntry, ...prev.slice(0, 999)]); // Keep last 100 logs
  }, []);

  const generateDeviceCode = useCallback(async () => {
    // Don't generate if we already have a valid device code
    if (oauthData && oauthData.deviceCode && oauthData.expiresIn) {
      const now = Date.now();
      const expirationTime = oauthData.expiresIn * 1000; // Convert to milliseconds
      if (now < expirationTime) {
        addDebugLog('INFO', 'Device code still valid, skipping generation');
        return;
      }
    }

    try {
      setStatus('generating');
      const requestData = {
        url: OAUTH_CONFIG.deviceAuthUrl,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        data: {
          client_id: OAUTH_CONFIG.clientId,
          scope: OAUTH_CONFIG.scope
        }
      };

      const response = await axios.post(OAUTH_CONFIG.deviceAuthUrl, {
        client_id: OAUTH_CONFIG.clientId,
        scope: OAUTH_CONFIG.scope
      }, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      const data = response.data;
      addRequestResponseLog(requestData, data);
      
      const expiresIn = data.expires_in || 1800; // Default to 30 minutes
      const expirationTime = Date.now() + (expiresIn * 1000);

      const oauthDataToSet = {
        deviceCode: data.device_code,
        userCode: data.user_code,
        verificationUrl: data.verification_uri,
        verificationUrlComplete: data.verification_uri_complete,
        expiresIn: expirationTime,
        interval: data.interval || 5
      };
      
      console.log('Setting OAuth data:', oauthDataToSet);
      setOauthData(oauthDataToSet);

      setStatus('pending');
      setPollingInterval(data.interval || 5);
      
      // Start polling for token
      startPolling(data.device_code, data.interval || 5);

      // Set timer to regenerate device code when it expires
      const timer = setTimeout(() => {
        addDebugLog('INFO', 'Device code expired, regenerating...');
        generateDeviceCode();
      }, expiresIn * 1000);

      setExpirationTimer(timer);

    } catch (err) {
      console.error('Error generating device code:', err);
      const requestData = {
        url: OAUTH_CONFIG.deviceAuthUrl,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        data: {
          client_id: OAUTH_CONFIG.clientId,
          scope: OAUTH_CONFIG.scope
        }
      };
      addRequestResponseLog(requestData, null, {
        message: err.message,
        response: err.response?.data
      });
      setStatus('error');
    }
  }, [addDebugLog, OAUTH_CONFIG.clientId, OAUTH_CONFIG.deviceAuthUrl, OAUTH_CONFIG.scope]);

  const startPolling = useCallback((deviceCode, interval) => {
    const poll = async () => {
      try {
        const requestBody = {
          client_id: OAUTH_CONFIG.clientId,
          device_code: deviceCode,
          grant_type: 'urn:ietf:params:oauth:grant-type:device_code'
        };
        
        addDebugLog('REQUEST', 'Polling for token', {
          url: OAUTH_CONFIG.tokenUrl,
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: requestBody
        });

        const response = await axios.post(OAUTH_CONFIG.tokenUrl, requestBody, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });

        if (response.data.access_token) {
          addDebugLog('SUCCESS', 'Access token received', {
            completeResponse: response.data
          });
          setStatus('success');
          setOauthData(prev => ({ ...prev, accessToken: response.data.access_token }));
          
          // Extract user name from userinfo endpoint and navigate to welcome screen
          getUserInfo(response.data.access_token).then(name => {
            setUserName(name);
            setCurrentPage('welcome');
          }).catch(error => {
            console.error('Error getting user info:', error);
            // Fallback to default name
            setUserName('User');
            setCurrentPage('welcome');
          });
          
          stopPolling();
        }
      } catch (err) {
        if (err.response?.status === 400 && err.response?.data?.error === 'authorization_pending') {
          // Still pending, continue polling
          addDebugLog('INFO', 'Authorization still pending, continuing to poll');
          setPollingTimer(setTimeout(poll, interval * 1000));
        } else if (err.response?.status === 400 && err.response?.data?.error === 'authorization_declined') {
          addDebugLog('ERROR', 'Authorization declined by user', err.response.data);
          setStatus('error');
          stopPolling();
        } else if (err.response?.status === 400 && err.response?.data?.error === 'expired_token') {
          addDebugLog('ERROR', 'Device code expired', err.response.data);
          setStatus('error');
          stopPolling();
        } else {
          console.error('Polling error:', err);
          addDebugLog('ERROR', 'Polling error', {
            error: err.message,
            response: err.response?.data
          });
          setStatus('error');
          stopPolling();
        }
      }
    };

    setPollingTimer(setTimeout(poll, interval * 1000));
  }, [addDebugLog, OAUTH_CONFIG.clientId, OAUTH_CONFIG.tokenUrl]);

  const stopPolling = useCallback(() => {
    if (pollingTimer) {
      clearTimeout(pollingTimer);
      setPollingTimer(null);
      addDebugLog('INFO', 'Polling stopped');
    }
  }, [pollingTimer, addDebugLog]);

  const resetOAuth = useCallback(() => {
    stopPolling();
    if (expirationTimer) {
      clearTimeout(expirationTimer);
      setExpirationTimer(null);
    }
    setOauthData(null);
    setStatus('idle');
    setPollingInterval(null);
    addDebugLog('INFO', 'OAuth flow reset');
  }, [stopPolling, addDebugLog, expirationTimer]);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      stopPolling();
      if (expirationTimer) {
        clearTimeout(expirationTimer);
      }
    };
  }, []); // Only cleanup on unmount

  // Render functions for different pages
  const renderAppsPage = () => (
    <div className="tv-content">
      <div className="apps-container">
        <h1 className="apps-title">Smart TV Apps</h1>
        <div className="apps-grid">
          {apps.map((app) => (
            <div 
              key={app.id} 
              className="app-card"
              onClick={app.name === 'NationalPost' ? () => handleAppSelect(app) : undefined}
              style={{ 
                '--app-color': app.color,
                cursor: app.name === 'NationalPost' ? 'pointer' : 'default',
                opacity: app.name === 'NationalPost' ? 1 : 0.5,
                filter: app.name === 'NationalPost' ? 'none' : 'grayscale(50%)'
              }}
            >
              <div className="app-icon">{app.icon}</div>
              <div className="app-name">{app.name}</div>
              <div className="app-description">{app.description}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAppDetailsPage = () => (
    <div className="tv-content">
      <div className="app-details-container">
        <div className="app-details-content">
          <div className="app-details-icon" style={{ '--app-color': selectedApp.color }}>
            {selectedApp.icon}
          </div>
          <h1 className="app-details-name">{selectedApp.name}</h1>
          <p className="app-details-description">{selectedApp.description}</p>
          <button className="login-button" onClick={handleLogin}>
            Authorize
          </button>
        </div>
      </div>
    </div>
  );

  const renderOAuthPage = () => (
    <div className="tv-content" style={{ overflow: 'hidden' }}>
      {/* App Name Header */}
              <div style={{
          width: '100%',
          textAlign: 'center',
          marginBottom: '20px'
        }}>
        <h1 style={{
          fontSize: '2rem',
          color: '#fff',
          margin: 0,
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
          fontWeight: 'bold'
        }}>
          {selectedApp ? selectedApp.name : 'TV OAuth Flow'}
        </h1>
      </div>

      {/* Single Screen Layout with Left and Right Content */}
      <div style={{
        display: 'flex',
        width: '100%',
        height: 'calc(100% - 80px)',
        gap: '20px',
        alignItems: 'stretch',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        padding: '20px'
      }}>
        {/* Left Side - Authorization Steps */}
        <div style={{
          flex: 2,
          display: 'flex',
          flexDirection: 'column',
          //justifyContent: 'center',
          overflow: 'hidden'
        }}>
          {status === 'generating' && !oauthData ? (
            // Show loading state while generating device code
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div className="instructions">
                <p>Initializing OAuth flow...</p>
                <p>Please wait while we generate your device code.</p>
              </div>
              
              <div style={{ marginTop: '30px' }}>
                <div style={{ 
                  fontSize: '1.2rem', 
                  padding: '15px 30px',
                  background: 'linear-gradient(45deg, #4CAF50, #45a049)',
                  borderRadius: '10px',
                  color: 'white',
                  opacity: 0.8
                }}>
                  Generating Device Code...
                </div>
              </div>
            </div>
          ) : oauthData ? (
            // Show OAuth flow when data is present
            <>
              <div className="instructions" style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                borderRadius: '15px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <h3 style={{
                  color: '#fff',
                  fontSize: '1.2rem',
                  marginBottom: '15px',
                  textAlign: 'center',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                }}>
                  📱 Authorization Steps
                </h3>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px'
                  }}>
                    <span style={{
                      background: 'linear-gradient(45deg, #4169E1, #2E4B8A)',
                      color: '#fff',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                      fontWeight: 'bold'
                    }}>
                      1
                    </span>
                    <span style={{ color: '#fff', fontSize: '0.95rem' }}>
                      Visit the verification URL below
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px'
                  }}>
                    <span style={{
                      background: 'linear-gradient(45deg, #4169E1, #2E4B8A)',
                      color: '#fff',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                      fontWeight: 'bold'
                    }}>
                      2
                    </span>
                    <span style={{ color: '#fff', fontSize: '0.95rem' }}>
                      Enter the user code shown on screen
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px'
                  }}>
                    <span style={{
                      background: 'linear-gradient(45deg, #4169E1, #2E4B8A)',
                      color: '#fff',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                      fontWeight: 'bold'
                    }}>
                      3
                    </span>
                    <span style={{ color: '#fff', fontSize: '0.95rem' }}>
                      Complete the authorization on your device
                    </span>
                  </div>
                </div>
              </div>

              {oauthData && oauthData.expiresIn && (
                <div style={{ 
                  marginTop: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 20px',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                    borderRadius: '25px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Animated background effect */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: `linear-gradient(90deg, transparent, ${timeLeft > 30 ? 'rgba(0, 255, 136, 0.1)' : timeLeft > 10 ? 'rgba(255, 170, 0, 0.1)' : 'rgba(255, 68, 68, 0.1)'}, transparent)`,
                      animation: 'slideRight 2s ease-in-out infinite',
                      zIndex: 0
                    }} />
                    
                    {/* Status indicator with multiple rings */}
                    <div style={{
                      position: 'relative',
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: timeLeft > 30 ? '#00ff88' : timeLeft > 10 ? '#ffaa00' : '#ff4444',
                        animation: 'pulse 1s ease-in-out infinite',
                        boxShadow: `0 0 12px ${timeLeft > 30 ? '#00ff88' : timeLeft > 10 ? '#ffaa00' : '#ff4444'}`,
                        position: 'relative'
                      }} />
                      <div style={{
                        position: 'absolute',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: `2px solid ${timeLeft > 30 ? '#00ff88' : timeLeft > 10 ? '#ffaa00' : '#ff4444'}`,
                        opacity: 0.3,
                        animation: 'expand 2s ease-in-out infinite'
                      }} />
                      <div style={{
                        position: 'absolute',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        border: `1px solid ${timeLeft > 30 ? '#00ff88' : timeLeft > 10 ? '#ffaa00' : '#ff4444'}`,
                        opacity: 0.2,
                        animation: 'expand 2s ease-in-out infinite 0.5s'
                      }} />
                    </div>
                    
                    <span style={{
                      fontSize: '0.9rem',
                      color: '#e0e0e0',
                      fontWeight: '400',
                      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                      letterSpacing: '0.5px',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      Renew in: <span style={{
                        color: timeLeft > 30 ? '#00ff88' : timeLeft > 10 ? '#ffaa00' : '#ff4444',
                        fontWeight: '600',
                        fontSize: '1.1rem',
                        textShadow: `0 0 8px ${timeLeft > 30 ? '#00ff88' : timeLeft > 10 ? '#ffaa00' : '#ff4444'}`,
                        animation: timeLeft <= 10 ? 'blink 0.5s ease-in-out infinite' : 'none',
                        letterSpacing: '0.5px'
                      }}>{timeLeft}s</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Buttons hidden as requested */}
              {/* <div style={{ marginTop: '20px' }}>
                <button 
                  className="button" 
                  onClick={generateDeviceCode}
                  disabled={status === 'generating'}
                >
                  Generate New Code
                </button>
                
                <button 
                  className="button" 
                  onClick={resetOAuth}
                  style={{ background: 'linear-gradient(45deg, #ff4444, #cc3333)' }}
                >
                  Reset
                </button>
              </div> */}



              <div style={{ marginTop: '15px' }}>
                {renderStatus()}
              </div>
            </>
          ) : (
            // Fallback state (shouldn't normally reach here)
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div className="instructions">
                <p>OAuth flow ready.</p>
                <p>Authorization process will start automatically.</p>
              </div>
              
              {/* Button hidden as requested */}
              {/* <div style={{ marginTop: '30px' }}>
                <button 
                  className="button" 
                  onClick={generateDeviceCode}
                  disabled={status === 'generating'}
                  style={{ 
                    fontSize: '1.2rem', 
                    padding: '15px 30px',
                    background: 'linear-gradient(45deg, #4CAF50, #45a049)'
                  }}
                >
                  Generate New Code
                </button>
              </div> */}
            </div>
          )}

          <div className="instructions" style={{ marginTop: '20px', fontSize: '0.9rem' }}>
            <p>This demo uses LoginRadius OAuth 2.0 Device Flow.</p>
          </div>
        </div>

        {/* Right Side - Authorization Code */}
        <div style={{
          flex: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          <div className="qr-section" style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
            borderRadius: '20px',
            padding: '20px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            overflow: 'hidden'
          }}>
                          {/* Removed "Authorization Code" title */}
            
            {status === 'generating' && !oauthData ? (
              // Show loading state for QR section
              <div style={{ 
                textAlign: 'center', 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
                <h4 style={{
                  color: '#e0e0e0',
                  marginBottom: '20px',
                  fontSize: '1.2rem',
                  fontWeight: '500',
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                  letterSpacing: '0.5px',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                }}>Scan QR Code</h4>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: '20px',
                  width: '160px',
                  height: '160px'
                }}>
                  <div style={{
                    color: '#888',
                    fontSize: '1rem',
                    textAlign: 'center'
                  }}>
                    Generating QR Code...
                  </div>
                </div>
                <div style={{ 
                  color: '#ccc',
                  fontSize: '0.9rem',
                  lineHeight: '1.4',
                  maxWidth: '250px',
                  textAlign: 'center'
                }}>
                  QR code will appear here once the authorization code is generated
                </div>
              </div>
            ) : oauthData ? (
              <>
                <div className="user-code" style={{
                  textAlign: 'center',
                  marginBottom: '15px'
                }}>
                  {oauthData.userCode}
                </div>
                
                <div className="verification-url" style={{
                  marginBottom: '15px',
                  textAlign: 'center'
                }}>
                  <strong style={{ 
                    color: '#e0e0e0', 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontSize: '1.1rem',
                    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                    fontWeight: '500',
                    letterSpacing: '0.5px',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                  }}>Verification URL:</strong>
                  {oauthData.verificationUrl ? (
                    <span style={{ color: '#87ceeb', wordBreak: 'break-all', fontSize: '1.3rem', lineHeight: '1.4', fontWeight: '500' }}>
                      {oauthData.verificationUrl}
                    </span>
                  ) : (
                    <span style={{ color: '#888', fontStyle: 'italic', fontSize: '1rem' }}>
                      URL will appear here once device code is generated
                    </span>
                  )}
                </div>

                                  {oauthData.verificationUrlComplete && (
                    <div className="qr-or-divider" style={{
                      textAlign: 'center',
                      margin: '15px 0'
                    }}>
                      <span style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        padding: '6px 15px',
                        borderRadius: '15px',
                        color: '#fff',
                        fontSize: '0.8rem'
                      }}>OR</span>
                    </div>
                  )}

                  {(oauthData.verificationUrlComplete || oauthData.verificationUrl) && (
                    <div style={{
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%'
                    }}>
                      <h4 style={{
                        color: '#e0e0e0',
                        marginBottom: '20px',
                        fontSize: '1.2rem',
                        fontWeight: '500',
                        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                        letterSpacing: '0.5px',
                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                      }}>Scan QR Code</h4>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: '20px'
                      }}>
                        <QRCode 
                          value={oauthData.verificationUrlComplete || oauthData.verificationUrl || 'https://example.com'}
                          size={160}
                          level="H"
                          includeMargin={false}
                          style={{
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                          }}
                        />
                      </div>
                      <div style={{
                        color: '#ccc',
                        fontSize: '0.9rem',
                        lineHeight: '1.4',
                        maxWidth: '250px',
                        textAlign: 'center'
                      }}>
                        Use your mobile device to scan this QR code for quick access
                      </div>
                    </div>
                  )}
              </>
            ) : (
              // Fallback state for QR section
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ 
                  fontSize: '1rem', 
                  padding: '20px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '10px',
                  color: '#ccc'
                }}>
                  Authorization code will appear here
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderWelcomePage = () => {
    const dummyArticles = [
      {
        id: 1,
        title: "Breaking: New AI Technology Revolutionizes Smart TV Experience",
        category: "Technology",
        time: "2 hours ago",
        image: "🤖",
        excerpt: "Latest artificial intelligence breakthroughs are transforming how we interact with our televisions, making the viewing experience more personalized than ever."
      },
      {
        id: 2,
        title: "Streaming Wars: NationalPost Announces Major Content Expansion",
        category: "Entertainment",
        time: "4 hours ago",
        image: "🎬",
        excerpt: "NationalPost reveals plans for 50 new original series and movies, promising to deliver unprecedented entertainment value to subscribers worldwide."
      },
      {
        id: 3,
        title: "Sports Update: Championship Finals Set for This Weekend",
        category: "Sports",
        time: "6 hours ago",
        image: "⚽",
        excerpt: "The highly anticipated championship finals are confirmed for this weekend, with record-breaking viewership expected across all platforms."
      },
      {
        id: 4,
        title: "Weather Alert: Unusual Weather Patterns Expected This Week",
        category: "Weather",
        time: "8 hours ago",
        image: "🌤️",
        excerpt: "Meteorologists predict unusual weather patterns across the region, with potential impacts on outdoor activities and travel plans."
      },
      {
        id: 5,
        title: "Health & Wellness: New Study Reveals Benefits of Screen Time Management",
        category: "Health",
        time: "10 hours ago",
        image: "💪",
        excerpt: "Research shows that proper screen time management can significantly improve mental health and overall well-being."
      },
      {
        id: 6,
        title: "Business News: Tech Giants Report Record Quarterly Earnings",
        category: "Business",
        time: "12 hours ago",
        image: "📈",
        excerpt: "Major technology companies announce record-breaking quarterly earnings, signaling strong growth in the digital entertainment sector."
      }
    ];

    return (
      <div className="tv-content">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '20px',
          overflow: 'hidden'
        }}>
          {/* Welcome Header */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            borderRadius: '20px',
            padding: '25px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '3rem',
              marginBottom: '15px',
              filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
            }}>
              🎉
            </div>
            
            <h1 style={{
              fontSize: '2rem',
              color: '#fff',
              marginBottom: '8px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
              fontWeight: 'bold'
            }}>
              Welcome, {userName}!
            </h1>
            
            <p style={{
              fontSize: '1rem',
              color: '#ccc',
              marginBottom: '15px',
              lineHeight: '1.4'
            }}>
              Welcome to your personalized TV experience.
            </p>
          </div>

          {/* News Articles Section */}
          <div style={{
            flex: 1,
            overflow: 'hidden'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              color: '#fff',
              marginBottom: '15px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
              fontWeight: 'bold'
            }}>
              📰 Latest News
            </h2>
            
            <div className="news-articles-container" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '15px',
              height: 'calc(100% - 60px)',
              overflow: 'auto',
              paddingRight: '10px'
            }}>
              {dummyArticles.map((article) => (
                <div key={article.id} className="news-article-card" style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                  borderRadius: '15px',
                  padding: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.3)';
                  e.target.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.05) 100%)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.2)';
                  e.target.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)';
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      fontSize: '2rem',
                      marginRight: '12px'
                    }}>
                      {article.image}
                    </div>
                    <div style={{
                      flex: 1
                    }}>
                      <div style={{
                        fontSize: '0.8rem',
                        color: '#00ff88',
                        fontWeight: 'bold',
                        marginBottom: '4px'
                      }}>
                        {article.category}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#888'
                      }}>
                        {article.time}
                      </div>
                    </div>
                  </div>
                  
                  <h3 style={{
                    fontSize: '1.1rem',
                    color: '#fff',
                    marginBottom: '10px',
                    fontWeight: 'bold',
                    lineHeight: '1.3'
                  }}>
                    {article.title}
                  </h3>
                  
                  <p style={{
                    fontSize: '0.9rem',
                    color: '#ccc',
                    lineHeight: '1.4',
                    margin: 0
                  }}>
                    {article.excerpt}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStatus = () => {
    switch (status) {
      case 'generating':
        return (
          <div className="status pending">
            <span className="loading"></span>
            Generating device code...
          </div>
        );
      case 'pending':
        return (
          <div className="status pending">
            <span className="loading"></span>
            Waiting for authorization... (Polling every {pollingInterval}s)
          </div>
        );
      case 'success':
        return (
          <div className="status success">
            ✅ Authorization successful! Access token received.
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="tv-screen">
      {/* TV Power On Overlay */}
      {!tvScreenVisible && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: tvPowerOn ? 
            'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.9) 100%)' : 
            '#000',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.5s ease'
        }}>
          <div style={{
            textAlign: 'center',
            color: tvPowerOn ? '#00ff88' : '#333',
            fontSize: '2rem',
            fontWeight: 'bold',
            textShadow: tvPowerOn ? '0 0 20px #00ff88' : 'none',
            transition: 'all 0.3s ease'
          }}>
            {tvPowerOn ? 'TV POWERING ON...' : 'TV OFF'}
          </div>
        </div>
      )}

      {/* Elegant TV Remote Control Sidebar */}
      <div style={{
        position: 'absolute',
        left: '40px',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '15px',
        background: 'linear-gradient(165deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '20px',
        padding: '25px 20px',
        boxShadow: 
          '0 15px 35px rgba(0, 0, 0, 0.8), ' +
          'inset 0 1px 0 rgba(255, 255, 255, 0.1), ' +
          'inset 0 -1px 0 rgba(0, 0, 0, 0.3)',
        minWidth: '100px',
        backdropFilter: 'blur(10px)',
        opacity: tvScreenVisible ? 1 : 0,
        transform: tvScreenVisible ? 'translateY(-50%)' : 'translateY(-50%) scale(0.8)',
        transition: 'all 0.8s ease'
      }}>
        {/* Remote Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '10px',
          padding: '8px 12px',
          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
          borderRadius: '10px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          fontSize: '0.7rem',
          color: '#888',
          fontWeight: 'bold',
          letterSpacing: '1px',
          width: '100%'
        }}>
          TV REMOTE
        </div>

        {/* Power Button */}
        <button 
          style={{
            background: 'linear-gradient(145deg, #ff3366, #cc2255)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#fff',
            padding: '12px',
            borderRadius: '50%',
            cursor: 'pointer',
            fontSize: '1.1rem',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            width: '55px',
            height: '55px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(255, 51, 102, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.15) translateY(-2px)';
            e.target.style.boxShadow = '0 8px 20px rgba(255, 51, 102, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1) translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(255, 51, 102, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
          }}
        >
          ⏻
        </button>

        {/* Channel Up */}
        <button 
          style={{
            background: 'linear-gradient(145deg, #3a3a3a, #2a2a2a)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#fff',
            padding: '12px',
            borderRadius: '50%',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 'bold',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            width: '50px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.15) translateY(-2px)';
            e.target.style.background = 'linear-gradient(145deg, #4a4a4a, #3a3a3a)';
            e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1) translateY(0)';
            e.target.style.background = 'linear-gradient(145deg, #3a3a3a, #2a2a2a)';
            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
          }}
        >
          ▲
        </button>

        {/* Channel Down */}
        <button 
          style={{
            background: 'linear-gradient(145deg, #3a3a3a, #2a2a2a)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#fff',
            padding: '12px',
            borderRadius: '50%',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 'bold',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            width: '50px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.15) translateY(-2px)';
            e.target.style.background = 'linear-gradient(145deg, #4a4a4a, #3a3a3a)';
            e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1) translateY(0)';
            e.target.style.background = 'linear-gradient(145deg, #3a3a3a, #2a2a2a)';
            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
          }}
        >
          ▼
        </button>

        {/* Volume Up */}
        <button 
          style={{
            background: 'linear-gradient(145deg, #3a3a3a, #2a2a2a)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#fff',
            padding: '12px',
            borderRadius: '50%',
            cursor: 'pointer',
            fontSize: '0.9rem',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            width: '50px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.15) translateY(-2px)';
            e.target.style.background = 'linear-gradient(145deg, #4a4a4a, #3a3a3a)';
            e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1) translateY(0)';
            e.target.style.background = 'linear-gradient(145deg, #3a3a3a, #2a2a2a)';
            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
          }}
        >
          🔊
        </button>

        {/* Volume Down */}
        <button 
          style={{
            background: 'linear-gradient(145deg, #3a3a3a, #2a2a2a)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#fff',
            padding: '12px',
            borderRadius: '50%',
            cursor: 'pointer',
            fontSize: '0.9rem',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            width: '50px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.15) translateY(-2px)';
            e.target.style.background = 'linear-gradient(145deg, #4a4a4a, #3a3a3a)';
            e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1) translateY(0)';
            e.target.style.background = 'linear-gradient(145deg, #3a3a3a, #2a2a2a)';
            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
          }}
        >
          🔉
        </button>

        {/* Home/Grid Button - Moved to bottom for easy access */}
        <button 
          onClick={handleBackToApps}
          style={{
            background: 'linear-gradient(145deg, #00cc88, #009966)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#fff',
            padding: '12px',
            borderRadius: '50%',
            cursor: 'pointer',
            fontSize: '0.9rem',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            width: '55px',
            height: '55px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0, 204, 136, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            gap: '1px'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.15) translateY(-2px)';
            e.target.style.boxShadow = '0 8px 20px rgba(0, 204, 136, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1) translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(0, 204, 136, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
          }}
        >
          <div style={{ fontSize: '0.8rem', lineHeight: '0.8' }}>━━━</div>
          <div style={{ fontSize: '0.8rem', lineHeight: '0.8' }}>━━━</div>
          <div style={{ fontSize: '0.8rem', lineHeight: '0.8' }}>━━━</div>
        </button>

        {/* Remote Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '10px',
          padding: '8px 12px',
          background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
          borderRadius: '10px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          fontSize: '0.6rem',
          color: '#666',
          letterSpacing: '0.5px',
          width: '100%'
        }}>
          PREMIUM
        </div>
      </div>
      
      <div className="tv-bezel" style={{
        opacity: tvScreenVisible ? 1 : 0,
        transform: tvScreenVisible ? 'scale(1)' : 'scale(0.95)',
        transition: 'all 1s ease'
      }}>
        {currentPage === 'apps' && renderAppsPage()}
        {currentPage === 'app-details' && renderAppDetailsPage()}
        {currentPage === 'oauth' && renderOAuthPage()}
        {currentPage === 'welcome' && renderWelcomePage()}
      </div>
      




      {/* Debug Sidebar */}
      <div className="debug-sidebar open">
        <div className="debug-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '15px 20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#fff' }}>Logs</h3>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px'
          }}>
            <button 
              className="debug-button"
              onClick={() => setDebugLogs([])}
              style={{
                background: 'linear-gradient(135deg, #4a4a4a 0%, #2a2a2a 100%)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#fff',
                padding: '10px 20px',
                borderRadius: '25px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                minWidth: '120px'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px) scale(1.02)';
                e.target.style.background = 'linear-gradient(135deg, #5a5a5a 0%, #3a3a3a 100%)';
                e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                e.target.style.border = '1px solid rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.background = 'linear-gradient(135deg, #4a4a4a 0%, #2a2a2a 100%)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
                e.target.style.border = '1px solid rgba(255, 255, 255, 0.2)';
              }}
            >
              <span style={{
                position: 'relative',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                🗑️ Clear
              </span>
            </button>
            <span className="log-count" style={{
              fontSize: '0.8rem',
              color: '#ccc',
              fontWeight: 'bold',
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '6px 12px',
              borderRadius: '4px'
            }}>
              {debugLogs.length} logs
            </span>
          </div>
        </div>
        
        <div className="debug-content">
          
          <div className="debug-logs">
            {debugLogs.length === 0 ? (
              <div className="no-logs">No debug logs yet. API requests and responses will appear here.</div>
            ) : (
                              debugLogs.map((log) => (
                  <div key={log.id} className={`debug-log debug-log-${log.type.toLowerCase()}`}>
                    <div className="log-header">
                      <span className="log-timestamp">{log.timestamp}</span>
                      <span className={`log-type log-type-${log.type.toLowerCase()}`}>
                        {log.type === 'request-response' ? 'API Call' : log.type}
                      </span>
                    </div>
                    <div className="log-message">{log.message}</div>
                    {log.type === 'request-response' ? (
                      <div className="log-data">
                        <div className="request-section">
                          <strong>Request:</strong>
                          <pre>{JSON.stringify(log.request, null, 2)}</pre>
                        </div>
                        <div className="response-section">
                          <strong>Response:</strong>
                          <pre>{JSON.stringify(log.response, null, 2)}</pre>
                        </div>
                        {log.error && (
                          <div className="error-section">
                            <strong>Error:</strong>
                            <pre>{JSON.stringify(log.error, null, 2)}</pre>
                          </div>
                        )}
                      </div>
                    ) : log.data && (
                      <div className="log-data">
                        <pre>{JSON.stringify(log.data, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
